import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Languages, Loader2, Crown, Type, Repeat, Check, RotateCcw, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const TRANSLATE_TARGETS = [
  { v: "en", l: "English" },
  { v: "te", l: "తెలుగు (Telugu)" },
  { v: "hi", l: "हिन्दी (Hindi)" },
  { v: "ta", l: "தமிழ் (Tamil)" },
  { v: "ml", l: "മലയാളം (Malayalam)" },
  { v: "kn", l: "ಕನ್ನಡ (Kannada)" },
];
// Which transliteration style applies to a source language (code or name).
const STYLE_FOR_LANG = {
  te: "tinglish", telugu: "tinglish", hi: "hinglish", hindi: "hinglish",
  ta: "tanglish", tamil: "tanglish", ml: "manglish", malayalam: "manglish",
  kn: "kanglish", kannada: "kanglish",
};
const STYLE_LABEL = {
  tinglish: "Tinglish · Telugu + English", hinglish: "Hinglish · Hindi + English",
  tanglish: "Tanglish · Tamil + English", manglish: "Malayalam + English",
  kanglish: "Kannada + English", roman: "Romanized (Latin)",
};

export default function LanguageModal({ open, onOpenChange, projectId, isPro, onApplied, variant, onReset, versions = [], activeVersionId }) {
  const [mode, setMode] = useState("translate");
  const [target, setTarget] = useState("en");
  const [busy, setBusy] = useState(false);
  // Which existing text version transliteration should read FROM.
  const [translitSource, setTranslitSource] = useState(activeVersionId || "original");

  // Build the list of versions available as a transliteration source.
  const sourceOptions = useMemo(() => {
    const opts = versions.filter((v) => v.kind !== "transliterate")
      .map((v) => ({ id: v.id, label: v.label || v.language, language: v.language || v.target }));
    if (!opts.some((o) => o.id === "original")) opts.unshift({ id: "original", label: "Original transcript", language: "original" });
    return opts;
  }, [versions]);

  const selectedSource = sourceOptions.find((o) => o.id === translitSource) || sourceOptions[0];
  const derivedStyle = STYLE_FOR_LANG[(selectedSource?.language || "").toLowerCase()] || "roman";

  const apply = async () => {
    setBusy(true);
    try {
      let res;
      if (mode === "translate") res = await api.transform(projectId, "translate", target, "original");
      else res = await api.transform(projectId, "transliterate", derivedStyle, translitSource);
      onApplied(res);
      toast.success(mode === "translate" ? "Captions translated" : `Transliterated to ${STYLE_LABEL[derivedStyle] || derivedStyle}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not transform the transcript.");
    }
    setBusy(false);
  };

  const switchTo = async (vid) => {
    try { const res = await api.activateVersion(projectId, vid); onApplied(res); toast.success("Caption text switched"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't switch version."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10 bg-[#0d0d0d] text-white" data-testid="language-modal">
        <DialogTitle className="flex items-center gap-2 font-display text-xl font-black"><Languages size={20} className="text-[#00F0FF]" /> Language &amp; script</DialogTitle>
        <DialogDescription className="sr-only">Translate or transliterate your caption transcript while keeping word timings in sync.</DialogDescription>

        {!isPro ? (
          <div className="mt-3 rounded-2xl border border-[#00F0FF]/30 bg-gradient-to-b from-[#8B5CF6]/10 to-transparent p-6 text-center" data-testid="language-upgrade">
            <Crown className="mx-auto mb-3 text-[#FFD400]" size={28} />
            <div className="font-display text-lg font-bold">Translate &amp; transliterate is a Pro feature</div>
            <p className="mx-auto mt-2 max-w-sm text-sm text-[#A1A1AA]">Translate into English, Telugu, Hindi, Tamil, Malayalam or Kannada — or romanize as Tinglish, Hinglish, Tanglish and more, with timing kept in sync.</p>
            <Link to="/pricing" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] px-6 py-3 text-sm font-black text-black transition-transform hover:scale-[1.02]"><Crown size={16} /> Upgrade to Pro</Link>
          </div>
        ) : (
          <div className="mt-2 space-y-5">
            {/* available versions switcher */}
            {versions.length > 1 && (
              <div data-testid="version-switcher">
                <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Caption text version</div>
                <div className="flex flex-wrap gap-2">
                  {versions.map((v) => (
                    <button key={v.id} onClick={() => switchTo(v.id)} data-testid={`version-${v.id}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${activeVersionId === v.id ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>
                      {activeVersionId === v.id && <Check size={12} className="mr-1 inline text-[#00F0FF]" />}{v.label || v.language}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {[{ k: "translate", l: "Translate", i: Type, d: "Change the meaning into another language" },
                { k: "transliterate", l: "Transliterate", i: Repeat, d: "Keep pronunciation, romanize the script" }].map((m) => (
                <button key={m.k} onClick={() => setMode(m.k)} data-testid={`lang-mode-${m.k}`}
                  className={`rounded-xl border p-4 text-left transition-colors ${mode === m.k ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                  <div className="flex items-center gap-2 font-bold"><m.i size={16} /> {m.l}</div>
                  <div className="mt-1 text-xs text-[#A1A1AA]">{m.d}</div>
                </button>
              ))}
            </div>

            {mode === "translate" ? (
              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Translate to</div>
                <div className="grid grid-cols-3 gap-2">
                  {TRANSLATE_TARGETS.map((t) => (
                    <button key={t.v} onClick={() => setTarget(t.v)} data-testid={`lang-target-${t.v}`}
                      className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-3 text-sm font-semibold transition-colors ${target === t.v ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>
                      {target === t.v && <Check size={14} className="text-[#00F0FF]" />} {t.l}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Transliterate from</div>
                  <div className="grid gap-2">
                    {sourceOptions.map((o) => (
                      <button key={o.id} onClick={() => setTranslitSource(o.id)} data-testid={`translit-source-${o.id}`}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors ${translitSource === o.id ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 hover:border-white/25"}`}>
                        <span className="text-sm font-semibold text-white">{o.label}</span>
                        {STYLE_FOR_LANG[(o.language || "").toLowerCase()] && (
                          <span className="flex items-center gap-1 text-xs text-[#A1A1AA]">→ {STYLE_LABEL[STYLE_FOR_LANG[(o.language || "").toLowerCase()]]}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 rounded-lg border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 px-3 py-2 text-sm" data-testid="translit-preview-chain">
                  <span className="font-semibold text-white">{selectedSource?.label}</span>
                  <ArrowRight size={14} className="text-[#8B5CF6]" />
                  <span className="font-semibold text-[#00F0FF]">{STYLE_LABEL[derivedStyle] || "Romanized"}</span>
                </div>
              </div>
            )}

            <p className="text-xs text-[#666]">Word-level timing stays synced. Every version is kept separately — switch the active caption text any time above; the original is never overwritten.</p>

            {variant && (
              <button onClick={() => { onReset?.(); onOpenChange(false); }} data-testid="lang-revert-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-2.5 text-sm font-semibold text-[#A1A1AA] transition-colors hover:border-white/30 hover:text-white">
                <RotateCcw size={15} /> Restore original transcript
              </button>
            )}

            <button onClick={apply} disabled={busy} data-testid="lang-apply-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-black transition-colors hover:bg-white/90 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />} Apply to transcript
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
